/**
 * Print mode (single-shot): Send prompts, output result, exit.
 *
 * Used for:
 * - `pi -p "prompt"` - text output
 * - `pi --mode json "prompt"` - JSON event stream
 */

import type { AssistantMessage, ImageContent } from "@caupulican/pi-ai";
import type { AgentSessionRuntime } from "../core/agent-session-runtime.ts";
import { flushRawStdout, writeRawStdout } from "../core/output-guard.ts";
import { projectSessionEventForJson } from "./json-event-projection.ts";
import { registerTerminationSignalHandlers } from "./termination-signals.ts";

/**
 * Sentinel prefix for the cumulative-usage line emitted to stderr in text print mode under
 * `--print-usage`. A spawner (subagent extension, auto-learn runner) greps stderr for this prefix
 * and `JSON.parse`s the remainder ({@link Usage}) to roll the child's cost up via
 * `pi.reportSpawnedUsage`. See the Cost Aggregation design.
 */
export const PRINT_USAGE_LINE_PREFIX = "__PI_USAGE__";

/**
 * Options for print mode.
 */
export interface PrintModeOptions {
	/** Output mode: "text" for final response only, "json" for all events */
	mode: "text" | "json";
	/** Array of additional prompts to send after initialMessage */
	messages?: string[];
	/** First message to send (may contain @file content) */
	initialMessage?: string;
	/** Images to attach to the initial message */
	initialImages?: ImageContent[];
	/**
	 * Emit the session's cumulative usage at exit so a spawner can roll this child's cost up.
	 * - text mode: one `__PI_USAGE__<json>` line to stderr (keeps stdout = the answer).
	 * - json mode: a terminal `{type:"result", usage}` event is always emitted regardless of this flag.
	 */
	printUsage?: boolean;
}

/**
 * Run in print (single-shot) mode.
 * Sends prompts to the agent and outputs the result.
 */
export async function runPrintMode(runtimeHost: AgentSessionRuntime, options: PrintModeOptions): Promise<number> {
	const { mode, messages = [], initialMessage, initialImages, printUsage } = options;
	let exitCode = 0;
	let session = runtimeHost.session;
	let unsubscribe: (() => void) | undefined;
	let disposed = false;

	const disposeRuntime = async (): Promise<void> => {
		if (disposed) return;
		disposed = true;
		unsubscribe?.();
		await runtimeHost.dispose();
	};

	const cleanupSignalHandlers = registerTerminationSignalHandlers((exitCode) => {
		void disposeRuntime().finally(() => process.exit(exitCode));
	});

	runtimeHost.setRebindSession(async () => {
		await rebindSession();
	});

	const rebindSession = async (): Promise<void> => {
		session = runtimeHost.session;
		await session.bindExtensions({
			mode: "print",
			commandContextActions: {
				waitForIdle: () => session.agent.waitForIdle(),
				newSession: async (newSessionOptions) => runtimeHost.newSession(newSessionOptions),
				fork: async (entryId, forkOptions) => {
					const result = await runtimeHost.fork(entryId, forkOptions);
					return { cancelled: result.cancelled };
				},
				navigateTree: async (targetId, navigateOptions) => {
					const result = await session.navigateTree(targetId, {
						summarize: navigateOptions?.summarize,
						customInstructions: navigateOptions?.customInstructions,
						replaceInstructions: navigateOptions?.replaceInstructions,
						label: navigateOptions?.label,
					});
					return { cancelled: result.cancelled };
				},
				switchSession: async (sessionPath, switchOptions) => {
					return runtimeHost.switchSession(sessionPath, switchOptions);
				},
				reload: async () => {
					await session.reload();
				},
			},
			onError: (err) => {
				console.error(`Extension error (${err.extensionPath}): ${err.error}`);
			},
		});

		unsubscribe?.();
		unsubscribe = session.subscribe((event) => {
			if (mode === "json") {
				writeRawStdout(`${JSON.stringify(projectSessionEventForJson(event))}\n`);
			} else if (event.type === "warning") {
				console.warn(`Warning: ${event.message}`);
			}
		});
	};

	try {
		if (mode === "json") {
			const header = session.sessionManager.getHeader();
			if (header) {
				writeRawStdout(`${JSON.stringify(header)}\n`);
			}
		}

		await rebindSession();

		if (initialMessage) {
			await session.prompt(initialMessage, { images: initialImages });
		}

		for (const message of messages) {
			await session.prompt(message);
		}

		if (mode === "text") {
			const state = session.state;
			const lastMessage = state.messages[state.messages.length - 1];

			if (lastMessage?.role === "assistant") {
				const assistantMsg = lastMessage as AssistantMessage;
				if (assistantMsg.stopReason === "error" || assistantMsg.stopReason === "aborted") {
					console.error(assistantMsg.errorMessage || `Request ${assistantMsg.stopReason}`);
					exitCode = 1;
				} else {
					for (const content of assistantMsg.content) {
						if (content.type === "text") {
							writeRawStdout(`${content.text}\n`);
						}
					}
				}
			}
		}

		// Emit cumulative session usage so a spawner can roll this child's cost into its own
		// footer (Cost Aggregation). json: a terminal authoritative `result` event; text: an
		// opt-in (`--print-usage`) sentinel line on stderr, leaving stdout = the answer.
		const cumulativeUsage = session.getCumulativeUsage();
		if (mode === "json") {
			writeRawStdout(`${JSON.stringify({ type: "result", usage: cumulativeUsage })}\n`);
		} else if (printUsage) {
			process.stderr.write(`${PRINT_USAGE_LINE_PREFIX}${JSON.stringify(cumulativeUsage)}\n`);
		}

		return exitCode;
	} catch (error: unknown) {
		console.error(error instanceof Error ? error.message : String(error));
		return 1;
	} finally {
		cleanupSignalHandlers();
		await disposeRuntime();
		await flushRawStdout();
	}
}
